import Axios, { AxiosError, AxiosRequestConfig, default as axios } from 'axios';
import Cookies from 'js-cookie';

import { useUserStore } from '@/stores/user';
import { environment } from '@/utils/environment';

export const API = Axios.create({
  baseURL: environment.getAPIUrl(),
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
  paramsSerializer: (params) => {
    const serializedParams: string[] = [];

    Object.entries(params).forEach(([key, value]) => {
      let serializedValue = value;

      // The API requires objects and arrays to be JSON strings as encodedURIComponents
      if (typeof value === 'object' || Array.isArray(value)) {
        serializedValue = JSON.stringify(value);
      }

      // Only add the param if the value is not undefined
      if (typeof value !== 'undefined') {
        serializedParams.push(`${key}=${encodeURIComponent(serializedValue)}`);
      }
    });

    return serializedParams.join('&');
  },
});

API.interceptors.request.use(function (config) {
  const hasOIDCToken = Cookies.get('__session');

  // By pass the localStorage if we have a __session cookie which means we are
  // already authenticated by the OIDC provider.
  if (!hasOIDCToken) {
    const expires = localStorage.getItem('__AADV_access_token_expires__');

    /**
     * Check if the access token is expired, if so logout the user
     */
    if (expires && new Date(expires) < new Date()) {
      const logout = useUserStore.getState().logout;

      logout(() => {
        window.location.href = '/';
      });

      return config;
    }

    /**
     * Add the access token to the request headers
     */
    const access_token = localStorage.getItem('__AADV_access_token__');

    if (access_token) {
      config.headers.Authorization = `Bearer ${access_token}`;
    }
  }

  return config;
});

// Add a response interceptor
API.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error: AxiosError) => {
    /**
     * Log error in the console
     */
    if (!Axios.isCancel(error)) {
      console.log('----------------------------');
      console.log('---- API RESPONSE ERROR ----');
      console.log('----------------------------');
      console.error(error);
      console.log('----------------------------');
      console.log('----------------------------');
      console.log('----------------------------');
    }

    if (
      error.response?.status === 401 &&
      window.location.pathname !== '/login_old'
    ) {
      await axios.post(environment.getLogoutURL());

      if (window.location.pathname !== '/') {
        window.location.href = `/?error=${encodeURIComponent('Je bent uitgelogd, log opnieuw in.')}`;
      }
    }

    if (error.response?.status === 500) {
      window.location.href = '/500';
    }

    return Promise.reject(error);
  }
);

// add a second `options` argument here if you want to pass extra options to each generated query
export const orvalAPI = <T>(
  config: AxiosRequestConfig,
  options?: AxiosRequestConfig
): Promise<T> => {
  const source = Axios.CancelToken.source();
  const promise = API({
    ...config,
    ...options,
    cancelToken: source.token,
  }).then(({ data }) => data);

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  promise.cancel = () => {
    source.cancel('Query was cancelled');
  };

  return promise;
};

// In some case with react-query and swr you want to be able to override the return error type so you can also do it here like this
export type ErrorType<Error> = AxiosError<Error>;

const baseURL = API.defaults.baseURL;

export { baseURL };
export default API;
